import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar as AvatarMui,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Inventory as InventoryIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  Straighten as SizeIcon,
  LocationOn as LocationIcon,
  Business as SiteIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as ReportIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { hasReadPermission, hasWritePermission } from "../../utils/menuFilter";

const PlotInventory = () => {
  const history = useHistory();
  const canRead = hasReadPermission('project.plot-inventory') || hasReadPermission('project');
  const canWrite = hasWritePermission('project.plot-inventory') || hasWritePermission('project');

  const [inventoryData, setInventoryData] = useState([
    {
      id: 1,
      plotNo: "P-101",
      siteName: "Green Meadows",
      sector: "A",
      block: "B1",
      plotAmount: 500000,
      bookingAmount: 50000,
      size: "30x40",
      status: "Available",
      totalPlots: 150,
      availablePlots: 120,
      bookedPlots: 25,
      soldPlots: 5,
      holdPlots: 0,
      inventoryValue: 75000000,
    },
    {
      id: 2,
      plotNo: "P-102",
      siteName: "Sunny Heights",
      sector: "B",
      block: "B2",
      plotAmount: 600000,
      bookingAmount: 60000,
      size: "35x45",
      status: "Booked",
      totalPlots: 200,
      availablePlots: 150,
      bookedPlots: 40,
      soldPlots: 8,
      holdPlots: 2,
      inventoryValue: 120000000,
    },
    {
      id: 3,
      plotNo: "P-103",
      siteName: "Lake View",
      sector: "A",
      block: "B1",
      plotAmount: 550000,
      bookingAmount: 55000,
      size: "32x42",
      status: "Available",
      totalPlots: 100,
      availablePlots: 80,
      bookedPlots: 15,
      soldPlots: 3,
      holdPlots: 2,
      inventoryValue: 55000000,
    },
    {
      id: 4,
      plotNo: "P-104",
      siteName: "Green Meadows",
      sector: "C",
      block: "B3",
      plotAmount: 750000,
      bookingAmount: 75000,
      size: "40x50",
      status: "Available",
      totalPlots: 80,
      availablePlots: 60,
      bookedPlots: 15,
      soldPlots: 3,
      holdPlots: 2,
      inventoryValue: 60000000,
    },
    {
      id: 5,
      plotNo: "P-105",
      siteName: "Sunny Heights",
      sector: "A",
      block: "B1",
      plotAmount: 450000,
      bookingAmount: 45000,
      size: "25x35",
      status: "Hold",
      totalPlots: 120,
      availablePlots: 90,
      bookedPlots: 20,
      soldPlots: 5,
      holdPlots: 5,
      inventoryValue: 54000000,
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchSite, setSearchSite] = useState("");
  const [searchSector, setSearchSector] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  // Helper functions
  const getFilteredInventory = () => {
    return inventoryData.filter((plot) => {
      const matchesSearch = searchQuery ?
        plot.plotNo.toLowerCase().includes(searchQuery.toLowerCase()) : true;
      const matchesSite = searchSite ? plot.siteName === searchSite : true;
      const matchesSector = searchSector ? plot.sector === searchSector : true;
      const matchesStatus = statusFilter === "all" || plot.status === statusFilter;
      return matchesSearch && matchesSite && matchesSector && matchesStatus;
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Available':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Booked':
        return { backgroundColor: '#e3f2fd', color: '#1976d2' };
      case 'Hold':
        return { backgroundColor: '#fff3e0', color: '#f57c00' };
      case 'Sold':
        return { backgroundColor: '#f3e5f5', color: '#7b1fa2' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getOccupancyRate = (available, total) => {
    return ((total - available) / total * 100).toFixed(1);
  };

  const handleView = (plot) => {
    setSelectedPlot(plot);
    setViewDialogOpen(true);
  };

  const handleEdit = (id) => {
    alert(`Edit Plot Inventory ID: ${id}`);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this plot inventory?")) {
      setInventoryData(inventoryData.filter((item) => item.id !== id));
      setSuccessMessage("Plot inventory deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search and filter handlers
  const handleSearch = () => {
    setPage(0);
    setSuccessMessage(`Search completed. Found ${getFilteredInventory().length} plot inventories.`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReset = () => {
    setSearchQuery("");
    setSearchSite("");
    setSearchSector("");
    setStatusFilter("all");
    setPage(0);
    setSuccessMessage("");
  };

  const filteredData = getFilteredInventory();

  // Calculate total inventory metrics
  const totalInventoryValue = inventoryData.reduce((sum, plot) => sum + plot.inventoryValue, 0);
  const totalPlots = inventoryData.reduce((sum, plot) => sum + plot.totalPlots, 0);
  const totalAvailable = inventoryData.reduce((sum, plot) => sum + plot.availablePlots, 0);
  const totalBooked = inventoryData.reduce((sum, plot) => sum + plot.bookedPlots, 0);
  const totalSold = inventoryData.reduce((sum, plot) => sum + plot.soldPlots, 0);

  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <AvatarMui sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </AvatarMui>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Plot Inventory. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InventoryIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            Plot Inventory Management
          </Typography>
        </Box>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold" color="primary">
                {formatCurrency(totalInventoryValue)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Inventory Value
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <SiteIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {totalPlots}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Plots
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <LocationIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold" color="info.main">
                {totalAvailable}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Available Plots
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <MoneyIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                {totalBooked}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Booked Plots
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <ReportIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold" color="error.main">
                {totalSold}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sold Plots
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Project Selector */}
      <Box sx={{ mb: 3 }}>
        <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
          <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel id="project-select-label">Select Project</InputLabel>
              <Select
                labelId="project-select-label"
                label="Select Project"
                value={searchSite}
                onChange={(e) => setSearchSite(e.target.value)}
              >
                <MenuItem value="">
                  <em>All Projects</em>
                </MenuItem>
                {[...new Set(inventoryData.map((item) => item.siteName))].map((site) => (
                  <MenuItem key={site} value={site}>
                    {site}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ textTransform: 'none' }}
            >
              Select Project
            </Button>
          </CardContent>
        </Card>
      </Box>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search Plot No..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Site Name</InputLabel>
              <Select
                value={searchSite}
                onChange={(e) => setSearchSite(e.target.value)}
                label="Site Name"
              >
                <MenuItem value="">All Sites</MenuItem>
                {[...new Set(inventoryData.map((p) => p.siteName))].map((site) => (
                  <MenuItem key={site} value={site}>
                    {site}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Sector</InputLabel>
              <Select
                value={searchSector}
                onChange={(e) => setSearchSector(e.target.value)}
                label="Sector"
              >
                <MenuItem value="">All Sectors</MenuItem>
                {[...new Set(inventoryData.map((p) => p.sector))].map((sector) => (
                  <MenuItem key={sector} value={sector}>
                    {sector}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Available">Available</MenuItem>
                <MenuItem value="Booked">Booked</MenuItem>
                <MenuItem value="Hold">Hold</MenuItem>
                <MenuItem value="Sold">Sold</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ flex: 1 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                sx={{ flex: 1 }}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Plot No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Site Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Sector</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Block</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Total Plots</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Available</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Booked</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Sold</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Occupancy %</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Inventory Value</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((plot, index) => (
                    <TableRow key={plot.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography fontWeight="medium" color="primary">
                          {plot.plotNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <SiteIcon sx={{ mr: 0.5, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{plot.siteName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`Sector ${plot.sector}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{plot.block}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {plot.totalPlots}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="success.main" fontWeight="medium">
                          {plot.availablePlots}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="warning.main" fontWeight="medium">
                          {plot.bookedPlots}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="error.main" fontWeight="medium">
                          {plot.soldPlots}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${getOccupancyRate(plot.availablePlots, plot.totalPlots)}%`}
                          size="small"
                          color={getOccupancyRate(plot.availablePlots, plot.totalPlots) > 70 ? "success" : "warning"}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <MoneyIcon sx={{ mr: 0.5, fontSize: 16, color: 'success.main' }} />
                          <Typography variant="body2" fontWeight="medium" color="success.main">
                            {formatCurrency(plot.inventoryValue)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              color="info"
                              size="small"
                              onClick={() => handleView(plot)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {canWrite ? (
                            <>
                              <Tooltip title="Edit Inventory">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleEdit(plot.id)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Inventory">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleDelete(plot.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <></>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No plot inventory found matching your criteria.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination

          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
            Plot Inventory Details - {selectedPlot?.plotNo}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPlot && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Site Name</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedPlot.siteName}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Sector & Block</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>Sector {selectedPlot.sector}, {selectedPlot.block}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Plot Size</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedPlot.size}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Plot Amount</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{formatCurrency(selectedPlot.plotAmount)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Total Plots</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedPlot.totalPlots}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Available Plots</Typography>
                <Typography variant="body1" color="success.main" sx={{ mb: 2 }}>{selectedPlot.availablePlots}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Booked Plots</Typography>
                <Typography variant="body1" color="warning.main" sx={{ mb: 2 }}>{selectedPlot.bookedPlots}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Sold Plots</Typography>
                <Typography variant="body1" color="error.main" sx={{ mb: 2 }}>{selectedPlot.soldPlots}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Hold Plots</Typography>
                <Typography variant="body1" color="info.main" sx={{ mb: 2 }}>{selectedPlot.holdPlots}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Occupancy Rate</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {getOccupancyRate(selectedPlot.availablePlots, selectedPlot.totalPlots)}%
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Total Inventory Value</Typography>
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  {formatCurrency(selectedPlot.inventoryValue)}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlotInventory;
